import { z } from 'zod'
import { note300, uuid } from './primitives.ts'

/** M16-N: observations with a photo (GK-022/23; docs/M16.md N). "J'ai vu ça sur le green 4": a disease spot, a wet corner, vandalism on a tee, a dry ring,
 *  recorded on an area with a picture, offline, in ten seconds. Each schema mirrors `core.command_type.payload_schema` in
 *  `20261201100300_observations.sql`; the contract check (ops/contracts/check.mjs) runs the same fixtures and random payloads through both.
 *  - `observation.record@1` is a FACT of the field (the notebook's own permission, measurement.record; offline-first, projected in
 *    `sync/projectors.observations.ts`; kiosk-allowed by rule 9). The photo is an ordinary `attachment.register@1` with `entity_type 'ops.observation'`.
 *  - `observation.link_job@1` is an INTENT of the office (job.plan): the follow-up job, an ordinary `job.create@1` that leaves `ops.job` untouched (rule 1),
 *    named once on the observation. The same job again is silent; another job is refused once one is linked. */
export const OBSERVATION_KINDS = ['disease', 'pest', 'damage', 'wet', 'dry', 'other'] as const
export type ObservationKind = (typeof OBSERVATION_KINDS)[number]
/** @TS: an instant as the device writes it (toISOString), the pattern the stored schema uses (the same as the water and notebook facts). */
const instant = z.string().regex(/^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9:.]+(Z|[+-][0-9]{2}:?[0-9]{2})$/)

export const observationRecordV1 = z.strictObject({
  observation_id: uuid, area_id: uuid, kind: z.enum(OBSERVATION_KINDS), note: note300.optional(), observed_at: instant.optional(),
})
export const observationLinkJobV1 = z.strictObject({ observation_id: uuid, job_id: uuid })

export type ObservationRecordV1 = z.infer<typeof observationRecordV1>
export type ObservationLinkJobV1 = z.infer<typeof observationLinkJobV1>
